<template>
	<div :id="'scrolltarget-' + fieldName" v-if="!isHidden" class="select-field">
		<FormKit
			:id="fieldId"
			:name="name"
			:data-field-name="fieldName"
			type="select"
			:label="label"
			:aria-invalid="hasError(fieldName) || null"
			:aria-errormessage="'error_' + fieldId"
			:disabled="readOnly"
			v-bind="$attrs"
			:wrapper-class="'c-global-checkout__form-section  field-' + fieldName"
			:class="[
				{ errorState: 'is-error' },
				{ 'is-placeholder': !value || value === placeholderText },
				$attrs.class
			]"
			:data-testid="'select-' + fieldName"
			v-model="value"
			@blur="onBlur"
		>
			<option
				v-for="(item, index) in optionsList"
				:key="item.key"
				:value="item.key || ''"
			>
				{{ item.value || item.key }}
			</option>
		</FormKit>

		<div :id="'error_' + fieldId" :data-tracking-name="trackingName">
			<span
				class="error show-error validation-error"
				v-if="hasErrorType(fieldName, 'requiredError')"
			>
				{{ requiredErrorText }}
			</span>
			<span class="error show-error api-validation-error" v-if="hasErrorType(fieldName, 'apiValidationError')">
				{{ apiValidationError }}
			</span>
		</div>
	</div>
</template>
<script>
import store from "shared/store";
import DropdownSingleSelect from "./dropdownSingleSelect.vue";
import { FormKit } from '@formkit/vue';

import {
	trackingFieldName,
	validateFieldState,
} from "multisite/services/checkoutHelperService.js";
import { addressTypes } from 'shared/settings/constants';

export default {
	name: "select-field",
	components: {
		DropdownSingleSelect,
		FormKit,
	},
	emits: ['update:modelValue', 'validate'],
	store,
	props: {
		modelValue: String,
		submitted: Boolean,
		errors: {
			type: Object,
			default: {},
		},
		/** @type {function(fieldName: string, state: Object)} */
		updateFieldError: {
			type: Function,
		},
		fieldId: {
			type: String,
			default: null,
		},
		label: {
			type: String,
			default: null,
		},
		name: {
			type: String,
			default: null,
		},
		fieldName: {
			type: String,
			default: null,
		},
		required: {
			type: Boolean,
			default: false,
		},
		options: {
			type: Array,
			default: [],
		},
		requiredErrorText: {
			type: String,
			default: "",
		},
		placeholderText: {
			type: String,
			default: "",
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		availableForAlternativeAddresses: {
			type: Boolean,
			default: false
		},
		addressType: {
			type: String,
			default: 'shippingAddress',
		},
	},
	data() {
		return {
			trackingName: trackingFieldName(this.fieldName),
			hasBlur: false,
			value: this.modelValue,
		};
	},
	computed: {
		optionsList() {
			return[{ key: null, value: this.placeholderText }, ...this.options];
		},
		isHidden() {
			return this.addressType !== addressTypes.SHIPPING_ADDRESS && !this.availableForAlternativeAddresses;
		},
		apiValidationError() {
			return this.errors[this.fieldName]?.apiValidationError ?? '';
		},
	},
	watch: {
		modelValue() {
			this.validateField();
		},
		value(newValue, oldValue) {
			if (newValue != oldValue) {
				this.$emit('update:modelValue', newValue);
			};
		},
		options(newVal) {
			if (newVal.length === 1) {
				this.value = newVal[0].key;
			}
		}
	},
	methods: {
		hasErrorType(item, type) {
			return (this.hasBlur || this.submitted) && this.errors[item]
				? this.errors[item][type]
				: false;
		},
		hasError(item) {
			return this.hasBlur || this.submitted
				? this.errors[item] && !this.errors[item].isValid
				: false;
		},
		validateField() {
			const validationResult = validateFieldState(this.modelValue, {
				required: this.required,
				maxLength: this.maxLength,
				validatePattern: this.validatePattern,
			});
			if (this.updateFieldError) {
				this.updateFieldError(this.fieldName, validationResult);
			}
			this.$emit('validate', {
				key: this.fieldName,
				value: validationResult,
			});
		},
		onBlur(event) {
			this.hasBlur = true;
		},
	},
	mounted() {

		this.validateField();
	},
};
</script>

<style lang="scss" scoped>
$mobile-view: 640px;
.mobile-select-field {
	display: none;

	@media (max-width: $mobile-view) {
		display: block;
	}
}

</style>

<style lang="scss">
@import "multisite/styles/settings/settings";
@import "multisite/styles/tools/tools";

$mobile-view: 640px; // borrowed from _c-global-checkout.scss

.select-field {
	.formkit-label {
		padding: 0;
	}
	.formkit-wrapper {
		max-width: none;
	}
	.formkit-outer {
		margin: 0;
	}
	.formkit-inner {
		box-shadow: var(--fk-border-box-shadow);
		outline: none;
		&:has(:focus-visible) {
			box-shadow: 0 0 0 1px var(--fk-color-border-focus);
			outline: 4px solid var(--ds-color-focus-default);
		}
	}
	select.formkit-input {
		border: none;
		outline: none;
		line-height: t-rem(21px); // necessary to make the FormKit fields match the other fields in the checkout

		&:focus {
			border: none;
		}
	}

	@media (min-width: $mobile-view) {
		.field-title {
			.formkit-inner {
				width: 50%;
			}
		}
	}
}
</style>
